import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../api";

const ResetPassword = () => {
  const apiUrl = process.env.REACT_APP_API_ENDPOINT;
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const validatePassword = (password) => {
    const errors = [];

    if (password.length < 8) {
      errors.push("Password must be at least 8 characters.");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter.");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("Password must contain at least one number.");
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push("Password must contain at least one special character.");
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");

    if (!token) {
      setError("Token not found.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const validationErrors = validatePassword(formData.password);
    if (validationErrors.length > 0) {
      setError(validationErrors.join(" "));
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(`${apiUrl}/api/auth/reset-password`, {
        token,
        newPassword: formData.password,
      });

      setSuccess(
        "Password has been reset successfully. Redirecting to login..."
      );
      setTimeout(() => navigate("/"), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="bg-light min-vh-100 d-flex align-items-center justify-content-center"
      style={{
        backgroundImage: "url('/assets/bg-signin-blue.png')",
        backgroundSize: "cover",
      }}
    >
      <div className="card shadow-lg" style={{ maxWidth: 400, width: "100%" }}>
        <div className="card-body">
          <h2 className="card-title text-center mb-3">Reset Password</h2>
          <p className="text-muted text-center">Enter your new password below.</p>

          {success ? (
            <div className="alert alert-success text-center">
              <p className="mb-2">{success}</p>
              <Link
                to="/"
                className="btn btn-outline-primary btn-sm"
              >
                Back to Sign In
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label
                  htmlFor="password"
                  className="form-label"
                >
                  New Password
                </label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    id="password"
                    className={`form-control ${
                      error ? "is-invalid" : ""
                    }`}
                    placeholder="Enter new password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i
                      className={`bi bi-eye${
                        showPassword ? "-slash" : ""
                      }`}
                    ></i>
                  </button>
                </div>
              </div>
              <div className="mb-3">
                <label
                  htmlFor="confirmPassword"
                  className="form-label"
                >
                  Confirm Password
                </label>
                <div className="input-group">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    id="confirmPassword"
                    className={`form-control ${
                      error ? "is-invalid" : ""
                    }`}
                    placeholder="Confirm password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                  >
                    <i
                      className={`bi bi-eye${
                        showConfirmPassword ? "-slash" : ""
                      }`}
                    ></i>
                  </button>
                </div>
              </div>
              {error && <div className="alert alert-danger py-2">{error}</div>}

              <div className="d-grid mb-3">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Resetting..." : "Reset Password"}
                </button>
              </div>

              <div className="text-center">
                Remembered your password? <Link to="/">Sign In</Link>
              </div>
            </form>
          )}
        </div>
      </div>

      {isSubmitting && (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center">
          <div className="text-center text-white">
            <div className="spinner-border mb-2" role="status" />
            <div>Processing...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResetPassword;
